import { useState, useEffect, useRef } from 'react';
import { insertURLParameter, sandhillsLogging, sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import { hostedStoreGatewayTypes } from '@Components/Utilities/SandhillsConstants';
import translator from '@Components/Utilities/Translator';
import TranslationText from '@Components/Utilities/TranslationText';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import ready from 'document-ready';
import GlobalPaymentsForm from '@Components/Shared/Payments/GlobalPaymentsForm';
import FirstDataForm from '@Components/Shared/Payments/FirstDataForm';
import SquareForm from '@Components/Shared/Payments/SquareForm';
import StripeForm from '@Components/Shared/Payments/StripeForm';
import PayPalButton from '@Components/Shared/Payments/PayPalButton';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';

const PaymentAuthorizationForm = (props) => {

    const {
        BidAgreement,
        PaymentGateways,
        PreAuthAmountText,
        PreAuthAmount,
        Currency,
        HoldOnly,
        TollFreeNumber,
        DealerCRMID,
        GetStateDropDown,
        GetCountryDropDown,
        IsBidderRegistration,
        autoCloseModal,
        onPaymentMethodAuthorized,
        onBiddingAgreementDataUpdated
    } = props;


    //global vars
    const payPalGateWay = PaymentGateways?.filter(g => g.GatewayType == hostedStoreGatewayTypes.PayPal)?.[0];
    const mainGateWay = PaymentGateways?.filter(g => g.GatewayType != hostedStoreGatewayTypes.PayPal)?.[0];
    const routes = useRecoilValue(atoms.routes);

    //state values
    //general
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [render, setRender] = useState(false);
    const [payFieldsError, setPayFieldsError] = useState(null);
    const [statesList, setStatesList] = useState(GetStateDropDown);

    //saved payments related
    const [paymentAuthorized, setPaymentAuthorized] = useState(BidAgreement?.UserIsApproved);

    //gateways ready/script loading
    const [mainGateWayReady, setMainGateWayReady] = useState(!mainGateWay);
    const [payPalGateWayReady, setPayPalGateWayReady] = useState(!payPalGateWay);
    const [gatewayScriptLoaded, setGatewayScriptLoaded] = useState(false);
    const [gatewayScriptIsLoading, setGatewayScriptIsLoading] = useState(false);

    //form data
    const [billingAddressData, setBillingAddressData] = useState({
        FirstName: props?.Firstname,
        LastName: props?.Lastname,
        CardHolderName: '',
        Email: props?.Email,
        Phone: props?.Phone,
        Address1: props?.Address1,
        Address2: props?.Address2,
        City: props?.City,
        Country: props?.Country,
        State: props?.State,
        PostalCode: props?.PostalCode
    });

    const currentBillingData = useRef(billingAddressData);

    const [validationErrors, setValidationErrors] = useState({
        FirstName: '',
        LastName: '',
        Email: '',
        Phone: '',
        Address1: '',
        City: '',
        Country: ''
    });

    //useEffects

    //main ready/ gateway script loading
    useEffect(() => {
        ready(() => {
            if (mainGateWay && mainGateWay.ScriptURL) {
                loadGateWayScript(mainGateWay.ScriptURL, mainGateWay.GatewayType);
            }
            else {
                setGatewayScriptLoaded(true);
            }
        });
    }, []);


    //gateways ready
    useEffect(() => {
        if (mainGateWayReady && payPalGateWayReady) {
            setLoading(false);
            setRender(true);
        }
    }, [mainGateWayReady, payPalGateWayReady]);

    useEffect(() => {
        //if we already loaded the script i.e. opened and closed adn then re-opened the modal then set payPalReady s owe can render it
        if (payPalGateWay && !payPalGateWayReady && gatewayScriptExists(payPalGateWay?.GatewayType)) {
            onPayPalReady();
        }
    }, [gatewayScriptLoaded]);

    //payment authorized:
    //to change parent props i.e.when you clsoe and reopen the payment auth form, we want to still know that the payment was authorized w/o extra ajax call
    useEffect(() => {

        if (paymentAuthorized) {
            onPaymentMethodAuthorized?.();
            //don't close the modal if we are opening the pre-bidding form
            if (autoCloseModal) {
                autoCloseModal();
            }
        }

    }, [paymentAuthorized]);

    //FUNCTIONS

    //Ready/Gateway script loading
    const onReady = () => {
        setMainGateWayReady(true);
    };

    const onPayPalReady = () => {
        setPayPalGateWayReady(true);
    };

    const gatewayScriptExists = (gatewayType) => {
        return document.getElementById(getGateWayScriptID(gatewayType));
    };

    const getGateWayScriptID = (gatewayType) => {
        return "gateway-script-id-" + gatewayType.replace(" ", "-");
    };

    const loadGateWayScript = (scriptURL, gatewayType) => {
        if (scriptURL && !gatewayScriptLoaded && !gatewayScriptIsLoading && !gatewayScriptExists(gatewayType)) {
            setGatewayScriptIsLoading(true);
            const script = document.createElement('script');
            script.setAttribute("id", getGateWayScriptID(gatewayType));
            script.type = 'text/javascript';
            script.src = scriptURL;
            script.async = true;
            script.onload = () => {
                setGatewayScriptLoaded(true);
                setGatewayScriptIsLoading(false);
            };
            script.onerror = () => {
                console.error(mainGateWay.GatewayType + ' script could not be loaded.');
                setGatewayScriptIsLoading(false);
            };

            document.body.appendChild(script);
        }
        else if (!gatewayScriptIsLoading && gatewayScriptExists(gatewayType)) {
            setGatewayScriptLoaded(true);
        }
    };

    //success calls - saving payments and processing auths

    const getTrimmedCustomerData = (billingData) => {
        return {
            Address: {
                FirstName: billingData.FirstName?.trim(),
                LastName: billingData.LastName?.trim(),
                Email: billingData.Email?.trim(),
                PhoneNumber: billingData.Phone?.trim(),
                Address1: billingData.Address1?.trim(),
                Address2: billingData.Address2?.trim(),
                City: billingData.City?.trim(),
                Country: billingData.Country?.trim(),
                StateOrProvince: billingData.State?.trim(),
                PostalCode: billingData.PostalCode?.trim()
            }
        };
    };

    const logError = (error) => {
        try {
            console.log(error);
            sandhillsLogging(routes.LogClientError, {
                ClientError: "TradeSites pay form error occurred for EventID: " + BidAgreement.EventOrSaleID.toString() + " with the error object: " + error
            });
        }
        catch (err) {
            // do nothing
        }
    };

    const authPaymentMethod = (response) => {
        setLoading(true);
        if (!IsBidderRegistration) {
            setMessage(translator.translate('sandhills.paymentprocessing'));
        }

        sandhillsRequest({
            url: routes.AuthorizePaymentMethod,
            data: {
                gatewayType: response.GatewayType,              //the gateway name
                paymentMethodID: response.PaymentMethodID,      //see AuctionInvoicesGatewayPaymentMethod.ID for explanation for each gateway
                dealerCRMID: DealerCRMID,                       //dealer crmid tied to the event
                customer: getTrimmedCustomerData(currentBillingData.current),   //billing address data
                verificationToken: response.VerificationToken,  //Square specific
                expirationDate: response.ExpirationDate,         //GP specific
                currencyCode: Currency
            },
            method: 'post',
            errorHandling: {
                showDetails: true,
                useConsole: true,
                useAlert: false
            }
        }).then((data) => {
            if (data && data.Success) {
                if (!IsBidderRegistration)
                    processAuth(data);
                else {
                    onPaymentMethodAuthorized?.(data.ID);
                }
            } else {
                setMessage('');
                setPayFieldsError('An error occurred while authorizing the payment method.');
                setLoading(false);
            }
        }).catch((error) => {
            setMessage('');
            setPayFieldsError('An error occurred while authorizing the payment method.');
            setLoading(false);
        });
    };

    const processAuth = (response) => {
        if (!loading) {
            setLoading(true);
        }
        if (!message) {
            setMessage(translator.translate('sandhills.paymentprocessing'));
        }

        let processAuthRequestData = {
            gatewayType: response.GatewayType,              //the gateway name
            referenceID: BidAgreement.EventOrSaleID,                  //referenceId we are processing auth for. Is either EventID or SaleID
            eventCategoryID: BidAgreement.EventCategoryID,          //event category id for determing type
            paymentMethodID: response.ID || response.PaymentMethodID, //the saved card payment method ID
            dealerCRMID: DealerCRMID,                       //dealer crmid tied to the event
            customer: getTrimmedCustomerData(currentBillingData.current),   //billing address data
            verificationToken: response.VerificationToken   //Square specific
        };

        sandhillsRequest({
            url: routes.ProcessAuth,
            data: processAuthRequestData,
            method: 'post',
            errorHandling: {
                showDetails: true,
                useConsole: true,
                useAlert: false
            }
        }).then((data) => {
            setLoading(false);
            if (data?.Success && !data?.Declined) {
                setPaymentAuthorized(true);
                setMessage(data.Message);
                if (data?.BiddingAgreementData) {
                    onBiddingAgreementDataUpdated?.(data.BiddingAgreementData);
                }
            }
            else {
                setMessage("We encountered an error processing your payment, any charges will be refunded. Please Try again later.");
            }
        }).catch((error) => {
            setMessage("We encountered an error processing your payment, any charges will be refunded. Please Try again later.");
            setLoading(false);
        });

    };

    //form related
    const updateStatesForCountry = (countryName) => {
        sandhillsRequest({
            url: routes.GetStatesByCountry,
            method: 'get',
            data: {
                'CountryName': countryName,
                'DefaultText': translator.translate('sandhills.registration.selectastateprovince'),
                'byStateCode': false
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response) {
                setStatesList(response || []);
            }
        });
    };

    const validateForm = (onSuccessCallback) => {

        let rules = [
            ruleRunner('FirstName', 'First Name', ValidationRules.required),
            ruleRunner('LastName', 'Last Name', ValidationRules.required),
            ruleRunner('Email', 'Email', ValidationRules.required),
            ruleRunner('Phone', 'Phone', ValidationRules.required),
            ruleRunner('Address1', 'Address', ValidationRules.required),
            ruleRunner('City', 'City', ValidationRules.required),
            ruleRunner('Country', 'Country', ValidationRules.required),
            ruleRunner('State', 'State', ValidationRules.requiredIf(billingAddressData.Country && ['US', 'CA'].includes(billingAddressData.Country))),
            ruleRunner('PostalCode', 'Postal Code', ValidationRules.requiredIf(billingAddressData.Country && ['US', 'CA'].includes(billingAddressData.Country)))
        ];

        let errors = run({
            ...billingAddressData
        }, rules);

        setValidationErrors(errors);

        if (Object.keys(errors).length <= 0) {
            // Passed basic validation, now check if address info matches CRM Contact
            setLoading(true);
            sandhillsRequest({
                url: routes.ValidateCardAddress,
                data: getTrimmedCustomerData(currentBillingData.current),
                method: 'post'
            }).then((data) => {
                setLoading(false);
                if (data?.Success) {
                    onSuccessCallback();
                }
                else {
                    setPayFieldsError('Your credit card billing address does not match your registration information. Please verify and try again.');
                }
            }).catch(() => {
                setLoading(false);
                setPayFieldsError('Your credit card billing address does not match your registration information. Please verify and try again.');
            });
        }
    };

    //paypal related
    const createPayPalOrder = () => {
        let paypalOrderRequest = insertURLParameter(payPalGateWay.CreateOrderUrl, "eventID", BidAgreement.EventOrSaleID);
        paypalOrderRequest = insertURLParameter(paypalOrderRequest, "preAuthAmt", parseInt(PreAuthAmount));
        return sandhillsRequest({
            url: paypalOrderRequest,
            method: 'post',
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            if (data && data.Success && data.OrderID) {
                return data.OrderID;
            } else {
                setMessage('Error creating PayPal order. Please refresh the page and try again.');
                return null;
            }
        }).catch(() => {
            setMessage('Error creating PayPal order. Please refresh the page and try again.');
            return null;
        });
    };

    const getPayPalOrder = (data) => {
        let fullResponse = { ...payPalGateWay };
        fullResponse.PaymentMethodID = data.orderID;
        processAuth(fullResponse);
    };

    const payPalFields = () => {
        return (
            <div>
                <div className='paypal-btn'>
                    <PayPalButton
                        loadSdk
                        locale={'en-US'}
                        ready={onPayPalReady}
                        createOrder={createPayPalOrder}
                        onApprove={getPayPalOrder}
                        scriptID={getGateWayScriptID(payPalGateWay.GatewayType)}
                        options={{
                            clientId: payPalGateWay.PublicKey,
                            merchantId: payPalGateWay.LoginID,
                            intent: payPalGateWay.PayPalIntent.toLowerCase(),
                            commit: true,
                            components: 'buttons',
                            disableFunding: 'paylater',
                            currency: payPalGateWay.PayPalCurrencyCode
                        }}
                        style={{
                            size: 'responsive',
                            layout: 'vertical',
                            color: 'gold',
                            shape: 'rect',
                            label: 'paypal',
                            tagline: false,
                            height: 32
                        }}
                    />
                </div>
            </div>
        );
    };

    //form fields
    const nonPayPalFields = () => {
        if (mainGateWay) {
            return (
                <>
                    {standardBillingFields()}
                    {standardCardInfoFields()}
                </>
            );
        }
        return null;
    };
    
    const basicTextField = (name, label, value, err, required, onChange) => {
        return (
            <div className='form-element'>
                <div className='input-wrapper'>
                    {required && <span className='required red' />}
                    <label htmlFor={name}>{label}</label>
                    <input
                        id={name}
                        name={name}
                        type='text'
                        value={value}
                        onChange={onChange}
                    />
                </div>
                <div className='center' style={{ width: '100%', marginTop: '5px' }}>
                    <span className='error'>{err}</span>
                </div>
            </div>
        );
    };

    const basicSelectField = (name, label, value, options, err, required, onChange) => {
        return (
            <div className='form-element'>
                <div className='input-wrapper'>
                    {required && <span className='required red' />}
                    <label htmlFor={name}>{label}</label>
                    <select id={name} onChange={onChange} name={name} value={value}>
                        {options.map((item) =>
                            <option key={item.Value + ' ' + item.Text} value={item.Value} disabled={item.Disabled}>{item.Text}</option>)}
                    </select>
                </div>
                <div className='center' style={{ width: '100%', marginTop: '5px' }}>
                    <span className='error'>{err}</span>
                </div>
            </div>
        );
    };

    const standardBillingFields = () => {
        return (
            <div className='billing-fields'>
                <h3>{translator.translate('sandhills.billingaddress')}</h3>
                <div className='form-row'>
                    {basicTextField('FirstName', translator.translate('sandhills.tradepubwebsite.mvc.firstname'), billingAddressData.FirstName, validationErrors.FirstName, true, (e) => {
                        setBillingAddressData(previousState => {
                            currentBillingData.current = { ...previousState, FirstName: e.target.value, CardHolderName: (e.target.value?.trim() + ' ' + previousState?.LastName?.trim()).trim() };
                            return { ...previousState, FirstName: e.target.value, CardHolderName: (e.target.value?.trim() + ' ' + previousState?.LastName?.trim()).trim() };
                        });
                        setValidationErrors((previousState) => {
                            return ({ ...previousState, FirstName: '' });
                        });
                    })}
                    {basicTextField('LastName', translator.translate('sandhills.tradepubwebsite.mvc.lastname'), billingAddressData.LastName, validationErrors.LastName, true, (e) => {
                        setBillingAddressData(previousState => {
                            currentBillingData.current = { ...previousState, LastName: e.target.value, CardHolderName: (previousState?.FirstName + ' ' + e.target.value).trim() };
                            return { ...previousState, LastName: e.target.value, CardHolderName: (previousState?.FirstName + ' ' + e.target.value).trim() };
                        });
                        setValidationErrors((previousState) => {
                            return ({ ...previousState, LastName: '' });
                        });
                    })}
                </div>
                <div className='form-row'>
                    {basicTextField('Email', translator.translate('sandhills.email'), billingAddressData.Email, validationErrors.Email, true, (e) => {
                        setBillingAddressData(previousState => {
                            currentBillingData.current = { ...previousState, Email: e.target.value };
                            return { ...previousState, Email: e.target.value };
                        });
                        setValidationErrors((previousState) => {
                            return ({ ...previousState, Email: '' });
                        });
                    })}
                    {basicTextField('Phone', translator.translate('sandhills.phone'), billingAddressData.Phone, validationErrors.Phone, true, (e) => {
                        setBillingAddressData(previousState => {
                            currentBillingData.current = { ...previousState, Phone: e.target.value };
                            return { ...previousState, Phone: e.target.value };
                        });
                        setValidationErrors((previousState) => {
                            return ({ ...previousState, Phone: '' });
                        });
                    })}
                </div>
                <div className='form-row'>
                    {basicTextField('Address1', translator.translate('sandhills.tradepubwebsite.mvc.address'), billingAddressData.Address1, validationErrors.Address1, true, (e) => {
                        setBillingAddressData(previousState => {
                            currentBillingData.current = { ...previousState, Address1: e.target.value };
                            return { ...previousState, Address1: e.target.value };
                        });
                        setValidationErrors((previousState) => {
                            return ({ ...previousState, Address1: '' });
                        });
                    })}
                    {basicTextField('Address2', translator.translate('sandhills.tradepubwebsite.mvc.addressline2'), billingAddressData.Address2, null, false, (e) => {
                        setBillingAddressData(previousState => {
                            currentBillingData.current = { ...previousState, Address2: e.target.value };
                            return { ...previousState, Address2: e.target.value };
                        });
                    })}
                </div>
                <div className='form-row'>
                    {basicTextField('City', translator.translate('sandhills.city'), billingAddressData.City, validationErrors.City, true, (e) => {
                        setBillingAddressData(previousState => {
                            currentBillingData.current = { ...previousState, City: e.target.value };
                            return { ...previousState, City: e.target.value };
                        });
                        setValidationErrors((previousState) => {
                            return ({ ...previousState, City: '' });
                        });
                    })}
                    {basicSelectField('Country', translator.translate('sandhills.country'), billingAddressData.Country, GetCountryDropDown, validationErrors.Country, true, (e) => {
                        updateStatesForCountry(GetCountryDropDown?.filter(c => c.Value == e.target.value)?.[0]?.Text);
                        setBillingAddressData(previousState => {
                            currentBillingData.current = { ...previousState, Country: e.target.value };
                            return { ...previousState, Country: e.target.value };
                        });
                        setValidationErrors((previousState) => {
                            return ({ ...previousState, Country: '' });
                        });
                    })}
                </div>
                <div className='form-row'>
                    {basicSelectField('State', translator.translate('sandhills.stateprovince'), billingAddressData.State, statesList, validationErrors.State, ((billingAddressData.Country && ['US', 'CA'].includes(billingAddressData.Country)) ? true : false), (e) => {
                        setBillingAddressData(previousState => {
                            currentBillingData.current = { ...previousState, State: e.target.value };
                            return { ...previousState, State: e.target.value };
                        });
                        setValidationErrors((previousState) => {
                            return ({ ...previousState, State: '' });
                        });
                    })}
                    {basicTextField('PostalCode', translator.translate('sandhills.postalcode'), billingAddressData.PostalCode, validationErrors.PostalCode, ((billingAddressData.Country && ['US', 'CA'].includes(billingAddressData.Country)) ? true : false), (e) => {
                        setBillingAddressData(previousState => {
                            currentBillingData.current = { ...previousState, PostalCode: e.target.value };
                            return { ...previousState, PostalCode: e.target.value };
                        });
                        setValidationErrors((previousState) => {
                            return ({ ...previousState, PostalCode: '' });
                        });
                    })}
                </div>
            </div>
        );
    };

    const standardCardInfoFields = () => {
        return (
            <div className='card-info-fields'>
                <h3>{translator.translate('sandhills.cardinformation')}</h3>
                {gatewaySpecificCardFields()}
            </div>
        );
    };

    //square specific
    const getSquareContact = () => {
        return {
            familyName: billingAddressData.LastName?.trim(),
            givenName: billingAddressData.FirstName?.trim(),
            email: billingAddressData.Email?.trim(),
            country: billingAddressData.Country?.trim(),
            region: billingAddressData.State?.trim(),
            city: billingAddressData.City?.trim(),
            addressLines: [billingAddressData.Address1?.trim(), billingAddressData.Address2?.trim() || ''],
            postalCode: billingAddressData.PostalCode?.trim(),
            phone: billingAddressData.Phone?.trim()
        };
    };

    //Stripe specific
    const getStripeContact = () => {
        return {
            address: {
                city: billingAddressData.City?.trim(),
                country: billingAddressData.Country?.trim(),
                line1: billingAddressData.Address1?.trim(),
                line2: billingAddressData.Address2?.trim(),
                postal_code: billingAddressData.PostalCode?.trim(),
                state: billingAddressData.State?.trim()
            },
            email: billingAddressData.Email?.trim(),
            name: (billingAddressData.FirstName?.trim() + " " + billingAddressData.LastName?.trim()).trim()
        };
    };

    //render gateways
    const gatewaySpecificCardFields = () => {
        if (mainGateWay?.GatewayType == hostedStoreGatewayTypes.GlobalPayments) {
            return (
                <GlobalPaymentsForm
                    {...mainGateWay}
                    ready={onReady}
                    success={authPaymentMethod}
                    validateForm={validateForm}
                    submitText={translator.translate('sandhills.tradepubwebsite.mvc.submit')}
                    hideNameField
                    showLogo
                    loading={loading}
                    setLoading={setLoading}
                    error={payFieldsError}
                    setError={setPayFieldsError}
                    logError={logError}
                />
            );
        }
        else if (mainGateWay?.GatewayType == hostedStoreGatewayTypes.Square) {
            return (
                <SquareForm
                    {...mainGateWay}
                    ready={onReady}
                    success={processAuth}
                    validateForm={validateForm}
                    showLogo
                    PreAuthAmount={PreAuthAmount}
                    Currency={Currency}
                    squareContact={getSquareContact()}
                    loading={loading}
                    setLoading={setLoading}
                    error={payFieldsError}
                    setError={setPayFieldsError}
                    logError={logError}
                />
            );
        }
        else if (mainGateWay?.GatewayType == hostedStoreGatewayTypes.FirstData) {
            return (
                <FirstDataForm
                    {...mainGateWay}
                    ready={onReady}
                    success={processAuth}
                    validateForm={validateForm}
                    showLogo
                    loading={loading}
                    setLoading={setLoading}
                    error={payFieldsError}
                    setError={setPayFieldsError}
                />
            );
        }
        else if (mainGateWay?.GatewayType == hostedStoreGatewayTypes.Stripe) {
            return (
                <StripeForm
                    {...mainGateWay}
                    ready={onReady}
                    success={processAuth}
                    validateForm={validateForm}
                    showLogo
                    StripeContact={getStripeContact()}
                    loading={loading}
                    setLoading={setLoading}
                    error={payFieldsError}
                    setError={setPayFieldsError}
                />
            );
        }
        return null;
    };

    const biddingAgreementAuth = () => {
        return (
            <div className='payment-auth-container'>
                {message ?
                    <>
                        <h4>{message}</h4>
                        {loading && <LoadingRing sxBox={{ 'justify-content': 'center' }} />}
                    </>
                    :
                    <>
                        <div className='container-header'>
                            <div className="border-bottom">
                                <p>{translator.translate('sandhills.auctionpreauthpendingapproval')}</p>
                                <p dangerouslySetInnerHTML={{ __html: PreAuthAmountText }} />
                                {HoldOnly ?
                                    <p className="underline">{translator.translate('sandhills.bidderpreauthhold')}</p>
                                    :
                                    <p>{translator.translate('sandhills.bidderpreauthcharge')}</p>
                                }
                                <p>{translator.translate('sandhills.paymentsmanagedbyauctioneer')}</p>
                                <p>{translator.translate('sandhills.bidderpreauthauctioneerapproval')}</p>
                                <p>{translator.translate('sandhills.bidderpreauthquestions')}&nbsp;{TollFreeNumber}</p>
                            </div>
                        </div>
                        {gatewayScriptLoaded &&
                            <div className={'bidding-agreement-payfields container-body' + (render ? '' : ' hide')}>
                                {payPalGateWay &&
                                    payPalFields()
                                }
                                {(payPalGateWay && mainGateWay) &&
                                    <div>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.or' />
                                    </div>
                                }
                                {mainGateWay &&
                                    nonPayPalFields()
                                }
                            </div>
                        }
                        {!render && loading && <LoadingRing sxBox={{ 'justify-content': 'center' }} />}
                    </>
                }
            </div>
        );
    };
    
    const bidderRegistrationAuth = () => {
        return (
            <div className='payment-auth-container'>
                <div className='container-header'>
                    <div className="border-bottom center">
                        <p dangerouslySetInnerHTML={{ __html: PreAuthAmountText }} />
                    </div>
                </div>
                {gatewayScriptLoaded &&
                    <div className={'container-body' + (render ? '' : ' hide')}>
                        {nonPayPalFields()}
                    </div>
                }
                {!render && loading && <LoadingRing sxBox={{ 'justify-content': 'center' }} />}
            </div>
        );
    };

    //main render fn
    return (
        IsBidderRegistration ? bidderRegistrationAuth() : biddingAgreementAuth()
    );
}

export default PaymentAuthorizationForm;